import Head from 'next/head';
import type { NextPage } from 'next';
import { Modules } from '@/components/Modules';
import { renderMetaTags } from 'react-datocms';
import { withSession } from '@zopauk/nextjs-auth';
import { useArticleQuery } from '@/utils/dato/generated';
import { PreviewToast } from '@/components/preview/Toast';
import { StructuredData } from '@/components/StructuredData';
import { buildBlogPostingSchema } from '@/utils/blogPostingSchema';
import { buildBreadcrumbSchema, getArticleBreadcrumbs } from '@/utils/breadcrumbs';
import { serverSideProps, ArticleContext, BlogModules } from '@/utils/models/article';
import { extractLandingPageFaqModules, generateLandingPageFaqSchema, safeJsonLdStringify } from '@/utils/schema';

const Article: NextPage<ArticleContext> = (context) => {
  const { data } = useArticleQuery({ slug: context.slug });
  const faqModules = extractLandingPageFaqModules(data?.article?.body?.blocks ?? []);
  const faqSchema = faqModules.length > 0 ? generateLandingPageFaqSchema(faqModules.flatMap((m) => m.faqs)) : null;

  return (
    <>
      <Head>
        {data?.article?.seo ? renderMetaTags(data.article.seo.concat(data.site.favicon)) : null}
        {data?.article && !context.preview ? (
          <StructuredData structuredData={buildBlogPostingSchema(data.article)} />
        ) : null}
        {faqSchema && !context.preview ? (
          <script dangerouslySetInnerHTML={{ __html: safeJsonLdStringify(faqSchema) }} type="application/ld+json" />
        ) : null}
        {data?.article ? (
          <StructuredData structuredData={buildBreadcrumbSchema(getArticleBreadcrumbs(data.article))} />
        ) : null}
      </Head>
      {data?.template?.modular ? (
        <Modules<BlogModules, ArticleContext> modules={data.template.modular} context={context} />
      ) : null}
      <PreviewToast />
    </>
  );
};

export const getServerSideProps = serverSideProps;

export default withSession(Article, { forceLogin: false });
